import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload, FileUploadSelectEvent, FileUploadRemoveEvent } from 'primereact/fileupload';
import { classNames } from 'primereact/utils';
import { useCreateSupportTicket, useUploadSupportTicketAttachment } from '../../hooks/useSupportTickets';
import { SupportTicket, SupportTicketAttachmentReference } from '../../models/supportTickets';
import { priorityOptions, productAreaOptions } from './supportTicketHelpers';

interface NewTicketForm {
  title: string;
  productArea: string;
  priority: string;
  description: string;
}

const defaultValues: NewTicketForm = {
  title: '',
  productArea: '',
  priority: 'medium',
  description: '',
};

const NewTicketDialog = (props: {
  visible: boolean;
  onHide: () => void;
  onCreated: (ticket: SupportTicket) => void;
}): JSX.Element => {
  const { visible, onHide, onCreated } = props;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewTicketForm>({ defaultValues });

  const [files, setFiles] = useState<File[]>([]);
  const fileUploadRef = useRef<FileUpload>(null);

  const createMutation = useCreateSupportTicket();
  const uploadMutation = useUploadSupportTicketAttachment();

  const errorObject = createMutation.error ?? uploadMutation.error;
  const error = errorObject
    ? errorObject instanceof Error
      ? errorObject.message
      : 'Failed to create the ticket.'
    : null;

  const close = () => {
    reset(defaultValues);
    setFiles([]);
    fileUploadRef.current?.clear();
    createMutation.reset();
    uploadMutation.reset();
    onHide();
  };

  const submit = handleSubmit(async (form) => {
    createMutation.reset();
    uploadMutation.reset();
    try {
      const attachments: SupportTicketAttachmentReference[] = [];
      for (const file of files) {
        const uploaded = await uploadMutation.mutateAsync(file);
        attachments.push({ url: uploaded.url, title: uploaded.title, contentType: uploaded.contentType });
      }

      const ticket = await createMutation.mutateAsync({
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        productArea: form.productArea || undefined,
        attachments,
      });

      onCreated(ticket);
      close();
    } catch {
      // Error surfaced via createMutation.error / uploadMutation.error.
    }
  });

  const isSubmitting = createMutation.isPending || uploadMutation.isPending;

  const footer = (
    <div className="flex justify-end gap-2">
      <Button label="Cancel" text onClick={close} disabled={isSubmitting} />
      <Button label="Create Ticket" icon="pi pi-check" onClick={submit} loading={isSubmitting} />
    </div>
  );

  return (
    <Dialog
      header="New Support Ticket"
      visible={visible}
      onHide={close}
      footer={footer}
      style={{ width: '40rem' }}
      breakpoints={{ '960px': '90vw' }}
    >
      <div className="flex flex-col gap-4">
        {error && <div className="text-red-600 text-sm">{error}</div>}

        <div className="flex flex-col gap-1">
          <label htmlFor="title" className="text-sm font-medium text-gray-700">
            Title
          </label>
          <Controller
            name="title"
            control={control}
            rules={{ required: 'Title is required.' }}
            render={({ field }) => (
              <InputText
                id="title"
                {...field}
                className={classNames({ 'p-invalid': errors.title })}
                placeholder="Short summary of the issue"
              />
            )}
          />
          {errors.title && <small className="p-error">{errors.title.message}</small>}
        </div>

        <div className="flex gap-4">
          <div className="flex flex-col gap-1 flex-1">
            <label htmlFor="productArea" className="text-sm font-medium text-gray-700">
              Product Area
            </label>
            <Controller
              name="productArea"
              control={control}
              render={({ field }) => (
                <Dropdown
                  id="productArea"
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  options={productAreaOptions}
                  placeholder="Select area"
                  showClear
                />
              )}
            />
          </div>

          <div className="flex flex-col gap-1 flex-1">
            <label htmlFor="priority" className="text-sm font-medium text-gray-700">
              Priority
            </label>
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <Dropdown
                  id="priority"
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  options={priorityOptions}
                />
              )}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="description" className="text-sm font-medium text-gray-700">
            Description
          </label>
          <Controller
            name="description"
            control={control}
            rules={{ required: 'Description is required.' }}
            render={({ field }) => (
              <InputTextarea
                id="description"
                {...field}
                rows={6}
                autoResize
                className={classNames({ 'p-invalid': errors.description })}
                placeholder="Describe what happened, steps to reproduce, and impact"
              />
            )}
          />
          {errors.description && <small className="p-error">{errors.description.message}</small>}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">Attachments (optional)</label>
          <FileUpload
            ref={fileUploadRef}
            name="attachments"
            multiple
            customUpload
            auto={false}
            chooseLabel="Choose files"
            uploadOptions={{ className: 'hidden' }}
            onSelect={(event: FileUploadSelectEvent) => setFiles(event.files)}
            onRemove={(event: FileUploadRemoveEvent) =>
              setFiles((current) => current.filter((file) => file !== event.file))
            }
            onClear={() => setFiles([])}
            emptyTemplate={
              <p className="m-0 text-sm text-gray-500">
                Drag and drop files here to attach, or click Choose files.
              </p>
            }
          />
        </div>
      </div>
    </Dialog>
  );
};

export default NewTicketDialog;
